import {
  Box,
  Flex,
  IconButton,
  Button,
  Text,
  Input,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";

import { FiEdit, FiTrash2, FiSearch, FiX } from "react-icons/fi";

import { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";

import FormModal from "./FormModal";
import { SessaoService } from "../../services/sessao.service";

import ContentCard from "../../components/common/ContentCard";
import { PageHeader } from "../../components/common/PageHeader";

export default function ListPage() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [rows, setRows] = useState<any[]>([]);
  const [editing, setEditing] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    numero: "",
    descricao: "",
    zona: "",
    cep: "",
    endereco: "",
    numero_endereco: "",
    bairro: "",
    cidade: "",
    uf: "",
  });

  const [openFilter, setOpenFilter] = useState<string | null>(null);

  async function load() {
    try {
      setLoading(true);
      const data = await SessaoService.findAll();

      const normalizedRows = Array.isArray(data)
        ? data
        : Array.isArray((data as any)?.data)
          ? (data as any).data
          : [];

      setRows(normalizedRows);
      // setRows(data);
    } catch {
      toast({ title: "Erro ao carregar sessões", status: "error" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleDelete(id: number) {
    if (!confirm("Deseja excluir esta sessão?")) return;

    try {
      await SessaoService.delete(id);
      toast({ title: "Sessão excluída com sucesso", status: "success" });
      load();
    } catch {
      toast({ title: "Erro ao excluir sessão", status: "error" });
    }
  }

  function renderFilter(field: string, label: string) {
    return openFilter === field ? (
      <Input
        size="sm"
        autoFocus
        placeholder={`Filtrar ${label}`}
        value={(filters as any)[field]}
        onChange={(e) => setFilters((prev) => ({ ...prev, [field]: e.target.value }))}
        onBlur={() => setOpenFilter(null)}
      />
    ) : (
      <Flex align="center" gap={2}>
        <Text fontSize="sm" fontWeight="600">{label}</Text>
        <IconButton
          aria-label="filtro"
          icon={<FiSearch />}
          size="xs"
          variant="ghost"
          onClick={() => setOpenFilter(field)}
        />
      </Flex>
    );
  }

  const filtered = useMemo(() => {
    return rows.filter((item) => {
      const check = (field: any, filter: string) =>
        String(field ?? "").toLowerCase().includes(filter.toLowerCase());

      return (
        check(item.numero, filters.numero) &&
        check(item.descricao, filters.descricao) &&
        check(item.zona_numero, filters.zona) &&
        check(item.cep, filters.cep) &&
        check(item.endereco, filters.endereco) &&
        check(item.numero_endereco, filters.numero_endereco) &&
        check(item.bairro, filters.bairro) &&
        check(item.cidade, filters.cidade) &&
        check(item.uf, filters.uf)
      );
    });
  }, [rows, filters]);

  const columns = [
    { name: renderFilter("numero", "Número"), selector: (r: any) => r.numero, minWidth: "120px" },
    { name: renderFilter("descricao", "Descrição"), selector: (r: any) => r.descricao, minWidth: "200px", wrap: true },
    { name: renderFilter("zona", "Zona"), selector: (r: any) => `${r.zona_numero ?? ""} - ${r.zona_descricao ?? ""}`, minWidth: "180px", wrap: true },
    { name: renderFilter("cep", "CEP"), selector: (r: any) => r.cep, minWidth: "120px" },
    { name: renderFilter("endereco", "Endereço"), selector: (r: any) => r.endereco, minWidth: "220px", wrap: true },
    { name: renderFilter("numero_endereco", "Nº"), selector: (r: any) => r.numero_endereco, minWidth: "90px" },
    { name: renderFilter("bairro", "Bairro"), selector: (r: any) => r.bairro, minWidth: "140px", wrap: true },
    { name: renderFilter("cidade", "Cidade"), selector: (r: any) => r.cidade, minWidth: "140px", wrap: true },
    { name: renderFilter("uf", "UF"), selector: (r: any) => r.uf, minWidth: "80px" },
    {
      name: "Ações",
      width: "140px",
      cell: (row: any) => (
        <Flex gap={2}>
          <IconButton icon={<FiEdit />} size="sm" colorScheme="blue" onClick={() => { setEditing(row); onOpen(); }} />
          <IconButton icon={<FiTrash2 />} size="sm" colorScheme="red" onClick={() => handleDelete(row.id)} />
        </Flex>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Sessões"
        onNew={() => {
          setEditing(null);
          onOpen();
        }}
        actions={
          <Button
            leftIcon={<FiX />}
            size="sm"
            variant="outline"
            colorScheme="red"
            onClick={() => setFilters({ numero: "", descricao: "", zona: "", cep: "", endereco: "", numero_endereco: "", bairro: "", cidade: "", uf: "" })}
          >
            Limpar filtros
          </Button>
        }
      />

      <ContentCard>
        <DataTable
          columns={columns}
          data={filtered}
          pagination
          progressPending={loading}
          highlightOnHover
          responsive
          noDataComponent="Nenhuma sessão encontrada"
        />
      </ContentCard>

      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        initialData={editing}
        onSave={async (data: any) => {
          try {
            if (editing) {
              await SessaoService.update(editing.id, data);
              toast({ title: "Sessão atualizada com sucesso", status: "success" });
            } else {
              await SessaoService.create(data);
              toast({ title: "Sessão criada com sucesso", status: "success" });
            }
            load();
          } catch {
            toast({ title: "Erro ao salvar sessão", status: "error" });
          } finally {
            onClose();
            setEditing(null);
          }
        }}
      />
    </Box>
  );
}